import { gql } from '@apollo/client';
import type { TariffViewModel } from '@/graphql/generated';
import { DEFAULT_PAGE_SIZE } from '@/lib/constants';
import { GenericAutocomplete, type AutocompleteConfig } from './generic-autocomplete';

export interface TariffAutocompleteProps {
	value: string;
	onChange: (value: string, tariff: TariffViewModel | null) => void;
	placeholder?: string;
	disabled?: boolean;
	className?: string;
	initialSelectedLabel?: string;
}

const TARIFFS_QUERY = gql`
	query TariffsAutocomplete(
		$where: TariffViewModelFilterInput
		$first: Int
		$after: String
		$order: [TariffViewModelSortInput!]
	) {
		tariffs(where: $where, first: $first, after: $after, order: $order) {
			edges {
				node {
					tariffId
					name
					version
				}
			}
			pageInfo {
				hasNextPage
				endCursor
			}
		}
	}
`;

export const getTariffDisplayName = (tariff: TariffViewModel): string => {
	return tariff.name || 'Unnamed Tariff';
};

export const getTariffSecondaryText = (tariff: TariffViewModel): string => {
	if (tariff.version) {
		return `v${tariff.version}`;
	}
	return '';
};

const createTariffAutocompleteConfig = (): AutocompleteConfig<TariffViewModel> => ({
	query: TARIFFS_QUERY,
	queryName: 'tariffs',
	searchFields: ['name'],
	orderBy: [{ name: 'ASC' }],
	getDisplayName: getTariffDisplayName,
	getSecondaryText: getTariffSecondaryText,
	getId: (tariff) => tariff.tariffId,
	pageSize: DEFAULT_PAGE_SIZE,
	enableDetails: true,
	entityType: 'tariffs',
});

export const TariffAutocomplete: React.FC<TariffAutocompleteProps> = ({
	value,
	onChange,
	placeholder = 'Search tariffs...',
	disabled = false,
	className = '',
	initialSelectedLabel
}) => {
	const config = createTariffAutocompleteConfig();

	return (
		<GenericAutocomplete<TariffViewModel>
			config={config}
			value={value}
			onChange={onChange}
			placeholder={placeholder}
			disabled={disabled}
			className={className}
			initialSelectedLabel={initialSelectedLabel}
		/>
	);
};
